/**
 * Drawer — the slide-in panel shell (docs/DUPLICATION_AUDIT.md, B22): backdrop, panel,
 * header with title and close button, body, footer. Fixes A24 for every drawer that uses
 * it: Escape closes it, it is announced as a dialog (`role="dialog"`, `aria-modal`,
 * labelled by its title), and the close button is "Close", not "multiplication sign".
 *
 *   <Drawer open={open} onClose={onClose} title="Add Custom Reason"
 *           footer={<><button …>Cancel</button><button …>Save</button></>}>
 *     …fields…
 *   </Drawer>
 *
 * Escape closes only the topmost open drawer, and only while focus is inside it (or on
 * nothing). A confirm dialog opened over a drawer holds focus, so Escape there does not
 * close the drawer behind it. On open the panel takes focus unless something inside
 * already has it (a drawer's own autofocus still wins, a moment later); on close, focus
 * goes back to whatever opened it.
 *
 * @param {boolean} open
 * @param {() => void} onClose — the ✕, the backdrop and Escape
 * @param {node} title
 * @param {node} [footer] — the footer's buttons
 * @param {'md'|'lg'|'xl'|'wide'} [size] — `${prefix}-drawer--${size}`
 * @param {string} [classPrefix] — stylesheet family, 'dp' by default ('ap' / 'rp' / 'up' /
 *        'ct' for the Areas, Residents, Users and Custom Event Text drawers)
 * @param {boolean} [backdrop] — false when the page renders its own backdrop
 * @param {'div'|'form'} [bodyAs] — 'form' puts the footer inside the form, so a
 *        type="submit" button submits it (the profile drawers)
 * @param {object} [bodyProps] — e.g. onSubmit, style, className additions
 * @param {node} [headerExtra] — rendered after the title, before the close button
 * @param {node} [subheader] — between the header and the body (a subtitle, a sticky
 *        search bar); it does not scroll with the body
 * @param {object} [footerProps] — e.g. a column-layout style for a two-row footer
 * @param {'div'|'aside'} [panelAs] — the panel element
 * @param {object} [panelStyle] — e.g. a narrower maxWidth
 * @param {string} [className] — extra classes on the panel
 * @param {string} [ariaLabel] — when the title is not plain text
 * @param {node} [headerStart] — replaces the title <h2> on the left of the header, for a
 *        header that is more than a title (title + subtitle, title + ⓘ tip). Pair it with
 *        `ariaLabel`, since there is then no title id to label the dialog by.
 * @param {boolean} [rawBody] — render `children` straight into the panel, with no body
 *        wrapper and no footer slot: for drawers whose own <form> (with its footer
 *        inside) is the panel's direct child and carries its own class.
 * @param {object|function} [panelRef] — also receives the panel element (a drawer that
 *        focuses its first field on open, or measures itself)
 *
 * Escape: a drawer with a state that Escape should clear first (a confirm step, a
 * preview, an active listen) handles it in a capture-phase keydown listener and calls
 * `e.preventDefault()`; the shell then leaves the drawer open.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import './drawer.css';

const openStack = [];

const Drawer = ({
    open, onClose, title, footer = null, size, classPrefix = 'dp', backdrop = true,
    bodyAs = 'div', bodyProps = null, headerExtra = null, subheader = null, footerProps = null,
    panelAs = 'div', panelStyle, className = '', ariaLabel, headerStart = null, rawBody = false,
    panelRef: externalPanelRef, children,
}) => {
    const titleId = useId();
    const panelRef = useRef(null);
    const setPanel = useCallback((el) => {
        panelRef.current = el;
        if (typeof externalPanelRef === 'function') externalPanelRef(el);
        else if (externalPanelRef) externalPanelRef.current = el;
    }, [externalPanelRef]);
    const keyRef = useRef({});
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    /* A closed panel is inert (docs/DOUBLE_SUBMIT_AUDIT.md). It stays mounted and takes
       300ms to slide out, and its form's `saving` has already reset by then, so without
       this a click in that window re-submitted the same Add (duplicate residents and
       devices). Set as a DOM property in a layout effect: React 18 has no `inert` prop
       (React 19 makes it boolean, where '' means false), and the close render comes
       from an await continuation, so a passive effect could land after the next click. */
    useLayoutEffect(() => {
        if (panelRef.current) panelRef.current.inert = !open;
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const key = keyRef.current;
        openStack.push(key);
        const opener = document.activeElement;
        const panel = panelRef.current;
        if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });

        const onKey = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            if (openStack[openStack.length - 1] !== key) return;
            const a = document.activeElement;
            if (a && a !== document.body && !panelRef.current?.contains(a)) return;
            onCloseRef.current?.();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            const i = openStack.lastIndexOf(key);
            if (i !== -1) openStack.splice(i, 1);
            // Give focus back to the opener, unless focus has already moved elsewhere. When
            // the opener is gone (a row-actions menu item unmounts with its menu), don't
            // leave focus inside the now-hidden panel.
            const inPanel = !!panel?.contains(document.activeElement);
            if (opener && opener !== document.body && opener.focus && document.contains(opener)
                && (!document.activeElement || document.activeElement === document.body || inPanel)) {
                opener.focus({ preventScroll: true });
            } else if (inPanel) {
                document.activeElement.blur();
            }
        };
    }, [open]);

    const p = `${classPrefix}-drawer`;
    const Body = bodyAs;
    const Panel = panelAs;
    const { className: bodyClass = '', ...restBody } = bodyProps || {};
    const footerEl = footer ? <div className={`${p}__footer`} {...(footerProps || {})}>{footer}</div> : null;

    return (
        <>
            {backdrop && open ? <div className={`${p}-backdrop`} onClick={onClose} aria-hidden="true" /> : null}
            <Panel
                ref={setPanel}
                className={`${p}${size ? ` ${p}--${size}` : ''}${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
                role="dialog"
                aria-modal="true"
                aria-hidden={open ? undefined : true}
                {...(ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-labelledby': titleId })}
                tabIndex={-1}
                style={panelStyle}
            >
                <div className={`${p}__header`}>
                    {headerStart || <h2 className={`${p}__title`} id={titleId}>{title}</h2>}
                    {headerExtra}
                    <button type="button" className={`${p}__close`} onClick={onClose} aria-label="Close">
                        <span aria-hidden="true">{'✕'}</span>
                    </button>
                </div>
                {subheader}
                {rawBody ? children : (
                    <Body className={`${p}__body${bodyClass ? ` ${bodyClass}` : ''}`} {...restBody}>
                        {children}
                        {bodyAs === 'form' ? footerEl : null}
                    </Body>
                )}
                {rawBody || bodyAs === 'form' ? null : footerEl}
            </Panel>
        </>
    );
};

export default Drawer;
